import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ChevronDown, ChevronRight, Plus, Trash2, Pencil, Check, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  getFinancialHealth, updateUnitCosts,
  addPotentialIncome, updatePotentialIncome, deletePotentialIncome
} from '@/lib/api';

const MONTH_NAMES = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function fmt(val) {
  if (val == null || isNaN(val)) return '$0';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Number(val));
}

function fmtFull(val) {
  if (val == null || isNaN(val)) return '$0';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Number(val));
}

/* ── Potential Income Items ─────────────────────────────────── */
function PotentialItems({ items, unitId, year, month, onRefresh }) {
  const [adding, setAdding] = useState(false);
  const [desc, setDesc] = useState('');
  const [amt, setAmt] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editDesc, setEditDesc] = useState('');
  const [editAmt, setEditAmt] = useState('');

  const handleAdd = async () => {
    const amount = parseFloat(amt);
    if (!amount) return;
    try {
      await addPotentialIncome(unitId, year, month, { description: desc.trim() || 'Potential', amount });
      setAdding(false); setDesc(''); setAmt('');
      onRefresh();
    } catch { toast.error('Failed to add'); }
  };

  const handleUpdate = async (id) => {
    try {
      await updatePotentialIncome(id, { description: editDesc, amount: parseFloat(editAmt) || 0 });
      setEditingId(null);
      onRefresh();
    } catch { toast.error('Failed to update'); }
  };

  const handleDelete = async (id) => {
    try {
      await deletePotentialIncome(id);
      onRefresh();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="mt-1 space-y-0.5 flex flex-col items-center">
      {items.map(p => (
        <div key={p.id} className="flex items-center gap-1 group/pot">
          {editingId === p.id ? (
            <>
              <Input value={editDesc} onChange={e => setEditDesc(e.target.value)} className="h-5 text-[10px] px-1 w-16" />
              <Input type="number" value={editAmt} onChange={e => setEditAmt(e.target.value)} className="h-5 text-[10px] px-1 w-14 text-right" />
              <button onClick={() => handleUpdate(p.id)} className="text-[#2f7d5a] hover:text-[#2f7d5a]/80" data-testid={`save-potential-${p.id}`}><Check className="h-3 w-3" /></button>
              <button onClick={() => setEditingId(null)} className="text-[#5b6773]"><X className="h-3 w-3" /></button>
            </>
          ) : (
            <>
              <span className="text-[10px] text-[#5b6773] truncate max-w-[60px]">{p.description || 'Potential'}</span>
              <span className="text-[10px] tabular-nums text-[#1f2933]">{fmt(p.amount)}</span>
              <button onClick={() => { setEditingId(p.id); setEditDesc(p.description); setEditAmt(String(p.amount)); }}
                className="opacity-0 group-hover/pot:opacity-100 text-[#5b6773]" data-testid={`edit-potential-${p.id}`}><Pencil className="h-2.5 w-2.5" /></button>
              <button onClick={() => handleDelete(p.id)}
                className="opacity-0 group-hover/pot:opacity-100 text-destructive" data-testid={`delete-potential-${p.id}`}><Trash2 className="h-2.5 w-2.5" /></button>
            </>
          )}
        </div>
      ))}
      {adding ? (
        <div className="flex items-center gap-1">
          <Input value={desc} onChange={e => setDesc(e.target.value)} placeholder="Label" className="h-5 text-[10px] px-1 w-16" />
          <Input type="number" value={amt} onChange={e => setAmt(e.target.value)} placeholder="$" className="h-5 text-[10px] px-1 w-14 text-right"
            onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }} />
          <button onClick={handleAdd} className="text-[#2f7d5a]" data-testid={`confirm-add-potential-${unitId}-${month}`}><Check className="h-3 w-3" /></button>
          <button onClick={() => { setAdding(false); setDesc(''); setAmt(''); }} className="text-[#5b6773]"><X className="h-3 w-3" /></button>
        </div>
      ) : (
        <button onClick={() => setAdding(true)} className="text-[10px] text-[#2d948c] hover:underline flex items-center gap-0.5"
          data-testid={`add-potential-${unitId}-${year}-${month}`}>
          <Plus className="h-2.5 w-2.5" /> Add potential
        </button>
      )}
    </div>
  );
}

/* ── Cost Editor (inline expand) ────────────────────────────── */
function CostEditor({ unit, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [rent, setRent] = useState(String(unit.cost_to_rent || ''));
  const [util, setUtil] = useState(String(unit.utilities_other || ''));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await updateUnitCosts(unit.unit_id, {
        cost_to_rent: parseFloat(rent) || 0,
        utilities_other: parseFloat(util) || 0,
      });
      onRefresh();
      setOpen(false);
    } catch { toast.error('Failed to save costs'); }
    setSaving(false);
  };

  if (!open) {
    return (
      <button onClick={() => { setOpen(true); setRent(String(unit.cost_to_rent || '')); setUtil(String(unit.utilities_other || '')); }}
        className="text-[10px] text-[#2d948c] hover:underline" data-testid={`edit-costs-${unit.unit_id}`}>
        {unit.total_cost > 0 ? 'Edit costs' : 'Set costs'}
      </button>
    );
  }

  return (
    <div className="space-y-1.5 p-2 bg-[#f3efe6]/50 rounded-md border mt-1" data-testid={`cost-editor-${unit.unit_id}`}>
      <div className="flex items-center gap-2">
        <label className="text-[10px] text-[#5b6773] w-20">Cost to rent</label>
        <Input type="number" value={rent} onChange={e => setRent(e.target.value)} className="h-6 text-xs px-1.5 w-24 text-right" data-testid={`cost-rent-${unit.unit_id}`} />
      </div>
      <div className="flex items-center gap-2">
        <label className="text-[10px] text-[#5b6773] w-20">Utilities & other</label>
        <Input type="number" value={util} onChange={e => setUtil(e.target.value)} className="h-6 text-xs px-1.5 w-24 text-right" data-testid={`cost-util-${unit.unit_id}`} />
      </div>
      <div className="flex gap-1">
        <Button size="sm" className="h-6 text-[10px] gap-1" onClick={save} disabled={saving} data-testid={`save-costs-${unit.unit_id}`}>
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Save
        </Button>
        <Button variant="ghost" size="sm" className="h-6 text-[10px]" onClick={() => setOpen(false)}>Cancel</Button>
      </div>
    </div>
  );
}

/* ── Unit Row ───────────────────────────────────────────────── */
function UnitRow({ unit, avgWindow, onRefresh }) {
  return (
    <tr className="border-b hover:bg-[#f3efe6]/30 transition-colors" data-testid={`unit-row-${unit.unit_id}`}>
      {/* Unit name */}
      <td className="py-2.5 px-3 min-w-[140px]">
        <p className="text-sm font-medium text-[#1f2933]">{unit.unit_number}</p>
        <p className="text-[10px] text-[#5b6773]">{unit.property_name}</p>
      </td>
      {/* Avg income */}
      <td className="py-2.5 px-3 text-center tabular-nums w-[130px]">
        {unit.insufficient_data ? (
          <div className="flex flex-col items-center gap-0.5" data-testid={`avg-income-${unit.unit_id}`}>
            <span className="text-sm font-semibold text-[#5b6773]">N/A</span>
            <span className="text-[10px] text-[#8a94a3] italic">&lt; 3 mo of data</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-0.5" data-testid={`avg-income-${unit.unit_id}`}>
            <span className="text-sm font-semibold text-[#1f2933]">{fmtFull(unit.avg_income)}</span>
            {unit.avg_months_used < avgWindow && (
              <span className="text-[10px] text-[#8a94a3]">{unit.avg_months_used}-mo avg</span>
            )}
          </div>
        )}
      </td>
      {/* Cost of unit — number + Edit button stacked & centered */}
      <td className="py-2.5 px-3 text-center w-[130px]">
        <div className="flex flex-col items-center gap-0.5">
          <span className="text-sm font-semibold tabular-nums text-[#1f2933]" data-testid={`total-cost-${unit.unit_id}`}>{fmtFull(unit.total_cost)}</span>
          <CostEditor unit={unit} onRefresh={onRefresh} />
        </div>
      </td>
      {/* Net */}
      <td className="py-2.5 px-3 text-center tabular-nums w-[130px]">
        <span className={`text-sm font-semibold ${unit.net >= 0 ? 'text-[#2f7d5a]' : 'text-destructive'}`} data-testid={`net-${unit.unit_id}`}>
          {fmtFull(unit.net)}
        </span>
      </td>
      {/* Display months (current + next 3) */}
      {unit.months.map((m) => (
        <td key={`${m.year}-${m.month}`} className="py-2.5 px-3 w-[140px] text-center align-top" data-testid={`month-cell-${unit.unit_id}-${m.year}-${m.month}`}>
          <div className="flex flex-col items-center gap-0.5">
            <div className="flex items-center justify-center gap-1.5">
              <span className="text-xs tabular-nums font-medium text-[#1f2933]">{fmt(m.confirmed)}</span>
              {m.new_income > 0 && (
                <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-700 text-[9px] font-semibold px-1.5 py-0.5 leading-none tabular-nums"
                  data-testid={`new-income-badge-${unit.unit_id}-${m.year}-${m.month}`}
                  title={`${fmt(m.new_income)} new income since last potential update`}>
                  +{fmt(m.new_income)}
                </span>
              )}
            </div>
            {m.potential_total > 0 && (
              <span className="text-[10px] text-[#2d948c]">+{fmt(m.potential_total)}</span>
            )}
            <PotentialItems items={m.potential_items} unitId={unit.unit_id} year={m.year} month={m.month} onRefresh={onRefresh} />
          </div>
        </td>
      ))}
    </tr>
  );
}

/* ── Main Page ──────────────────────────────────────────────── */
export default function FinancialHealthPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { setData(await getFinancialHealth()); }
    catch { toast.error('Failed to load financial data'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    try { setData(await getFinancialHealth()); }
    catch { /* silent */ }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="h-6 w-6 animate-spin text-[#5b6773]" />
    </div>
  );

  if (!data) return <p className="text-[#5b6773] text-center py-12">Failed to load data</p>;

  const { totals, units, display_months, avg_window_months } = data;

  return (
    <div className="space-y-6" data-testid="financial-health-page">
      <div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight text-[#1f2933]">Financial Health</h1>
        <p className="text-sm text-[#5b6773]">Unit viability analysis — averages based on YTD data</p>
      </div>

      {/* ── Totals Card ─────────────────────────────────── */}
      <Card className="shadow-sm" data-testid="totals-card">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-[#1f2933] text-white">
                  <th className="py-2.5 px-3 text-[10px] uppercase tracking-wider font-semibold w-[140px]">Totals</th>
                  <th className="py-2.5 px-3 text-[10px] uppercase tracking-wider font-semibold text-center w-[130px]">Avg Income</th>
                  <th className="py-2.5 px-3 text-[10px] uppercase tracking-wider font-semibold text-center w-[130px]">Cost</th>
                  <th className="py-2.5 px-3 text-[10px] uppercase tracking-wider font-semibold text-center w-[130px]">Net</th>
                  {display_months.map(dm => (
                    <th key={`${dm.year}-${dm.month}`} className="py-2.5 px-3 text-[10px] uppercase tracking-wider font-semibold text-center w-[140px]">
                      {MONTH_NAMES[dm.month]} {dm.year}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="bg-[#1f2933]/[0.04] font-semibold">
                  <td className="py-2.5 px-3 text-xs text-[#1f2933]">All Units</td>
                  <td className="py-2.5 px-3 text-center tabular-nums text-sm text-[#1f2933]" data-testid="total-avg-income">{fmtFull(totals.total_avg_income)}</td>
                  <td className="py-2.5 px-3 text-center tabular-nums text-sm text-[#1f2933]" data-testid="total-cost">{fmtFull(totals.total_cost)}</td>
                  <td className={`py-2.5 px-3 text-center tabular-nums text-sm ${totals.total_net >= 0 ? 'text-[#2f7d5a]' : 'text-destructive'}`} data-testid="total-net">
                    {fmtFull(totals.total_net)}
                  </td>
                  {totals.months.map(mt => (
                    <td key={`t-${mt.year}-${mt.month}`} className="py-2.5 px-3 text-center" data-testid={`total-month-${mt.year}-${mt.month}`}>
                      <span className="text-sm tabular-nums text-[#1f2933]">{fmt(mt.total_confirmed)}</span>
                      {mt.total_potential > 0 && (
                        <div className="text-[10px] text-[#2d948c] tabular-nums">
                          +{fmt(mt.total_potential)} pot.
                        </div>
                      )}
                      {mt.total_potential > 0 && (
                        <div className="text-[10px] text-[#5b6773] tabular-nums">
                          = {fmt(mt.total_confirmed_plus_potential)}
                        </div>
                      )}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* ── Units Table ─────────────────────────────────── */}
      <Card className="shadow-sm" data-testid="units-table-card">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-[#2d948c]/[0.08] border-b">
                  <th className="py-2 px-3 text-[10px] uppercase tracking-wider font-semibold text-[#1f2933] w-[140px]">Unit</th>
                  <th className="py-2 px-3 text-[10px] uppercase tracking-wider font-semibold text-[#1f2933] text-center w-[130px]">Avg Income</th>
                  <th className="py-2 px-3 text-[10px] uppercase tracking-wider font-semibold text-[#1f2933] text-center w-[130px]">Cost of Unit</th>
                  <th className="py-2 px-3 text-[10px] uppercase tracking-wider font-semibold text-[#1f2933] text-center w-[130px]">Net</th>
                  {display_months.map(dm => (
                    <th key={`h-${dm.year}-${dm.month}`} className="py-2 px-3 text-[10px] uppercase tracking-wider font-semibold text-[#1f2933] text-center w-[140px]">
                      {MONTH_NAMES[dm.month]} {dm.year}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {units.map(u => (
                  <UnitRow key={u.unit_id} unit={u} avgWindow={avg_window_months} onRefresh={refresh} />
                ))}
                {units.length === 0 && (
                  <tr><td colSpan={4 + display_months.length} className="py-12 text-center text-[#5b6773]">No units found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
